import type { $Fetch } from 'ofetch';
import type { Avaliacao, AvaliacaoStatus, UpsertAvaliacaoCommand } from '@/domain/model/Avaliacao';
import type { AvaliacaoQuestao, AddQuestaoToAvaliacaoCommand } from '@/domain/model/AvaliacaoQuestao';
import type { Page } from '@/domain/model/Page';
import type { AvaliacaoGateway } from '@/application/port/out/AvaliacaoGateway';
import type { ApiResponse, SpringPage } from '@/infrastructure/http/types';
import { assertApiSuccess } from '@/infrastructure/http/assertApiSuccess';

const PATH = '/api/v1/avaliacoes';

export class HttpAvaliacaoGateway implements AvaliacaoGateway {
  constructor(private readonly client: $Fetch) {}

  async list(p: { disciplineId?: string; status?: AvaliacaoStatus; page?: number; size?: number }): Promise<Page<Avaliacao>> {
    const params: Record<string, string | number> = {};
    if (p.disciplineId) params['disciplineId'] = p.disciplineId;
    if (p.status) params['status'] = p.status;
    if (typeof p.page === 'number') params['page'] = p.page;
    if (typeof p.size === 'number') params['size'] = p.size;
    const res = await this.client<ApiResponse<SpringPage<Avaliacao>>>(PATH, { method: 'GET', params });
    assertApiSuccess(res);
    const d = res.data;
    return { content: d.content, number: d.number, size: d.size, totalElements: d.totalElements, totalPages: d.totalPages, first: d.first, last: d.last };
  }

  async get(id: string): Promise<Avaliacao> {
    const res = await this.client<ApiResponse<Avaliacao>>(`${PATH}/${encodeURIComponent(id)}`, { method: 'GET' });
    assertApiSuccess(res);
    return res.data;
  }

  async create(command: UpsertAvaliacaoCommand): Promise<Avaliacao> {
    const res = await this.client<ApiResponse<Avaliacao>>(PATH, { method: 'POST', body: command });
    assertApiSuccess(res);
    return res.data;
  }

  async update(id: string, command: UpsertAvaliacaoCommand): Promise<Avaliacao> {
    const res = await this.client<ApiResponse<Avaliacao>>(
      `${PATH}/${encodeURIComponent(id)}`, { method: 'PUT', body: command },
    );
    assertApiSuccess(res);
    return res.data;
  }

  // Fix audit F.2 P0-1: backend é PATCH /publicar (PT-BR) — POST /publish dava 404
  async publish(id: string): Promise<Avaliacao> {
    const res = await this.client<ApiResponse<Avaliacao>>(
      `${PATH}/${encodeURIComponent(id)}/publicar`, { method: 'PATCH' },
    );
    assertApiSuccess(res);
    return res.data;
  }

  async archive(id: string): Promise<Avaliacao> {
    const res = await this.client<ApiResponse<Avaliacao>>(
      `${PATH}/${encodeURIComponent(id)}/arquivar`, { method: 'PATCH' },
    );
    assertApiSuccess(res);
    return res.data;
  }

  async delete(id: string): Promise<void> {
    // BE retorna 204 No Content (sem envelope) — não chamar assertApiSuccess
    await this.client<void>(
      `${PATH}/${encodeURIComponent(id)}`, { method: 'DELETE' },
    );
  }

  // F.2.2b sub-resource — backend AvaliacaoController.addQuestao/listQuestoes/removeQuestao
  async listQuestoes(avaliacaoId: string): Promise<AvaliacaoQuestao[]> {
    const res = await this.client<ApiResponse<AvaliacaoQuestao[]>>(
      `${PATH}/${encodeURIComponent(avaliacaoId)}/questoes`, { method: 'GET' },
    );
    assertApiSuccess(res);
    return res.data;
  }

  async addQuestao(avaliacaoId: string, command: AddQuestaoToAvaliacaoCommand): Promise<AvaliacaoQuestao> {
    const res = await this.client<ApiResponse<AvaliacaoQuestao>>(
      `${PATH}/${encodeURIComponent(avaliacaoId)}/questoes`, { method: 'POST', body: command },
    );
    assertApiSuccess(res);
    return res.data;
  }

  async removeQuestao(avaliacaoId: string, questaoId: string): Promise<void> {
    // BE retorna 204 No Content (sem envelope) — não chamar assertApiSuccess
    await this.client<void>(
      `${PATH}/${encodeURIComponent(avaliacaoId)}/questoes/${encodeURIComponent(questaoId)}`,
      { method: 'DELETE' },
    );
  }
}
